// L6 tramo 1 · Lógica PURA del reparto del neto de una factura entre las líneas de facturación
// candidatas (F4/D4 de `lineas-facturacion-diseno-detallado-l3.md`). Sin React, sin DB → testeable.
//
// Eje ATRIBUCIÓN (ortogonal al cobro): cuánto del neto de UNA factura SII se aplica a cada línea
// (`facturas_sii_vinculaciones.monto_aplicado` por `linea_id`). La garantía DURA de Σ ≤ neto es el
// candado `fsv_b_bloquea_sobre_atribuir_trg` (AFTER, DEFERRED, 10.261); estas funciones son la ayuda
// EN VIVO del modal (10.177: muestran el saldo y evitan el viaje obvio, pero NO son la fuente de
// verdad). Reusa la tolerancia del núcleo de saldo (10.1) — misma moneda por construcción.

import { TOLERANCIA_MISMA_MONEDA } from "../cobro/resolverSaldo";

// Clave estable de una línea candidata: `linea_id` si ya existe; si la línea todavía NO existe (mes que
// se factura por primera vez), una clave sintética por concepto (negocio+tipo+mes+ano+tramo). El modal
// resuelve esa clave a `linea_id` recién al guardar (crea el elemento del array → trigger L1 → línea).
// Si `l` es un envoltorio que ya trae su `clave` calculada (el modal arma uno con `{ concepto, clave,
// linea_id, esPropio }` para conservar el concepto original junto a la línea candidata), se devuelve tal
// cual — recalcularla leyendo `l.negocio_id`/`l.tipo`/`l.ano`/`l.mes` del envoltorio es incorrecto porque
// esos campos viven en `l.concepto`, no en el primer nivel (caso dos torres sin `linea_id`: todas las
// líneas sin `clave` propia colapsarían a la misma clave sintética "undefined").
export const claveLinea = (l) => {
  if (l?.clave != null) return l.clave;
  return l?.linea_id != null ? `L:${l.linea_id}` : `C:${l?.negocio_id}:${l?.tipo}:${l?.ano}:${l?.mes}:${l?.tramo ?? 1}`;
};

const leer = (aplicadoPorClave, k) => {
  const v = aplicadoPorClave instanceof Map ? aplicadoPorClave.get(k) : aplicadoPorClave?.[k];
  return Number(v) || 0;
};

// Peso en UF de una línea candidata, para la precarga proporcional (D5). Prioriza el valor en UF —
// gestión + contenidos, fórmula completa de `carteleria-modelo-importe-y-edificios-dos-torres.md` §1
// (`useVFDataLoading.js`) — porque es lo exacto contra la orden de compra. `null` si la línea NO tiene
// representación íntegra en UF (gestión en USD/CLP, o contenidos en otra moneda que la de gestión): el
// llamador cae a pesos para TODAS las líneas del reparto, nunca mezcla unidades entre ellas.
const pesoUF = (c = {}) => {
  // Gestión (+contenidos, `feeGestionUF`/`feeContenidosUF` ya vienen gateados a null si su moneda no es
  // UF): si hay contenidos y su parte no es UF, la línea no está ÍNTEGRA en UF — no es "casi toda UF".
  if (c.feeGestionUF != null) {
    if (c.incluye_contenidos && c.feeContenidosUF == null) return null;
    return (Number(c.feeGestionUF) || 0) + (Number(c.feeContenidosUF) || 0);
  }
  // OPEX / CAPEX / línea ya creada en `lineas_facturacion`: la cuota entera está en UF si así lo dice su
  // propia moneda (`valor_cuota`/`moneda_cuota`, expuestos igual por los tres orígenes del loader).
  if (c.moneda_cuota === "UF" && c.valor_cuota != null) return Number(c.valor_cuota) || 0;
  return null;
};

// Peso en pesos (fallback): la cuota de referencia ya convertida que expone cada candidato (10.19/10.27,
// mismo campo que usa el resto de la pantalla — `getImporteFacturable`/badge de divergencia).
const pesoPesos = (c = {}) => Number(c.importe) || 0;

// Reparto proporcional al peso de cada línea (D5), enteros, Σ = neto exacto: cada línea (salvo la
// primera) redondea su parte normal; la primera absorbe el residuo del redondeo para que la suma cierre
// exacta con el neto. `null` si no hay pesos usables (todos 0 o faltantes) — el llamador mantiene el
// reparto anterior ("la primera línea toma el neto entero").
const repartoProporcional = (lineas, netoN) => {
  const conceptos = lineas.map((l) => l?.concepto ?? l);
  const pesosUF = conceptos.map(pesoUF);
  const usarUF = pesosUF.every((p) => p != null && p > 0);
  const pesos = usarUF ? pesosUF : conceptos.map(pesoPesos);
  const sumaPesos = pesos.reduce((acc, p) => acc + (Number(p) || 0), 0);
  if (!(sumaPesos > 0)) return null;
  const montos = {};
  let sumaResto = 0;
  lineas.forEach((l, i) => {
    if (i === 0) return;
    const share = Math.round((netoN * (Number(pesos[i]) || 0)) / sumaPesos);
    montos[claveLinea(l)] = share;
    sumaResto += share;
  });
  montos[claveLinea(lineas[0])] = netoN - sumaResto;
  return montos;
};

/**
 * Reparto INICIAL de montos por línea al abrir/seleccionar una factura.
 * - Si la factura YA tiene atribución (Σ aplicado > 0): cada línea arranca en su monto aplicado (D4:
 *   permite reajustar montos entre líneas mientras Σ no supere el neto).
 * - Si NO tiene atribución (aplicado 0) y hay más de una línea: reparto PROPORCIONAL al peso de cada
 *   línea (D5, `repartoProporcional`/`pesoUF`/`pesoPesos` arriba). Si ningún peso es usable, cae al
 *   comportamiento anterior: la PRIMERA línea (la primaria del negocio propio, el modal la ordena así)
 *   toma el NETO ENTERO y el resto 0 — también el camino con una sola línea.
 * @param {{lineas:Array, aplicadoPorClave:(Object|Map), neto:number}} args
 * @returns {Object} montos por clave de línea.
 */
export const calcularRepartoInicial = ({ lineas = [], aplicadoPorClave = {}, neto = 0 } = {}) => {
  const totalAplicado = lineas.reduce((acc, l) => acc + leer(aplicadoPorClave, claveLinea(l)), 0);
  const montos = {};
  if (totalAplicado > 0) {
    for (const l of lineas) montos[claveLinea(l)] = leer(aplicadoPorClave, claveLinea(l));
    return montos;
  }
  const netoN = Number(neto) || 0;
  if (lineas.length > 1) {
    const proporcional = repartoProporcional(lineas, netoN);
    if (proporcional) return proporcional;
  }
  lineas.forEach((l, i) => {
    montos[claveLinea(l)] = i === 0 ? netoN : 0;
  });
  return montos;
};

/**
 * Estado del reparto EN VIVO: Σ aplicada, saldo, validez y errores legibles (sin catch mudo, 10.177).
 * @returns {{suma:number, saldo:number, ok:boolean, errores:string[]}}
 */
export const validarReparto = ({ lineas = [], montos = {}, neto = 0, tolerancia = TOLERANCIA_MISMA_MONEDA } = {}) => {
  const errores = [];
  const vistas = new Set();
  let suma = 0;
  for (const l of lineas) {
    const k = claveLinea(l);
    if (vistas.has(k)) errores.push(`Línea repetida: ${l.label || k}`);
    vistas.add(k);
    const raw = montos[k];
    const m = Number(raw);
    if (raw != null && raw !== "" && (Number.isNaN(m) || m < 0)) errores.push(`Monto inválido en ${l.label || k}`);
    if (Number.isFinite(m) && m > 0) suma += m;
  }
  const netoN = Number(neto) || 0;
  const saldo = netoN - suma;
  if (suma > netoN + tolerancia) {
    errores.push(`La suma aplicada (${Math.round(suma)}) supera el neto de la factura (${Math.round(netoN)})`);
  }
  return { suma, saldo, ok: errores.length === 0, errores };
};

/**
 * "Aplicar el resto a…": asigna a la línea elegida el saldo restante (neto − Σ de las OTRAS líneas),
 * nunca negativo. Preserva los demás montos.
 */
export const aplicarResto = ({ lineas = [], montos = {}, neto = 0, claveDestino } = {}) => {
  if (claveDestino == null) return { ...montos };
  const netoN = Number(neto) || 0;
  const sumaOtras = lineas.reduce((acc, l) => {
    const k = claveLinea(l);
    if (k === claveDestino) return acc;
    const m = Number(montos[k]);
    return acc + (Number.isFinite(m) && m > 0 ? m : 0);
  }, 0);
  return { ...montos, [claveDestino]: Math.max(0, netoN - sumaOtras) };
};
